import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  useMediaQuery,
  useTheme,
  InputAdornment,
  Divider,
  IconButton
} from "@mui/material";
import { 
  authenticateUser, 
  GetUserById, 
  ResetPassword, 
  SendMailForgotPassword, 
  UpdateUserData, 
  UserLogin, 
  verifyOTP
} from "../../service/user_service";
import { AlertError, AlertSuccess } from "../../component/popupAlert";
import Cookies from "js-cookie";
import { RiLockPasswordLine } from "react-icons/ri";
import { FaRegUser } from "react-icons/fa";
import bg_login from "../../images/luxury_bg_login.png";
import { MdOutlineEmail } from "react-icons/md";
import { Visibility, VisibilityOff } from '@mui/icons-material';
import "./style.css";

const LoginPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [mode, setMode] = useState("login");
  const [userData, setUserData] = useState({
    username: "",
    password: "",
    email: "",
    otp: ""
  });

  const [passwordVisibility, setPasswordVisibility] = useState({
    password: false,
    confirmPassword: false
  });

  const [newPassword, setNewPassword] = useState({
    password: "",
    confirmPassword: ""
  });

  useEffect(() => {
    // Check URL or route to set initial mode if needed
    const path = window.location.pathname;
    if (path.includes("forgotpassword")) {
      setMode("forgotpassword");
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setNewPassword(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const togglePasswordVisibility = (field) => {
    setPasswordVisibility(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleLogin = async () => {
    if (!userData.username || !userData.password) {
      AlertError("Login Error", "Please enter username and password");
      return;
    }

    try {
      const token = await UserLogin(userData);
      if (token) {
        Cookies.set("token", token, { expires: 1 });
        await fetchUserData(token);
        navigate("/thewonderblueadmin_admin");
      }
    } catch (error) {
      AlertError("Login Failed", "Invalid username or password");
    }
  };

  const fetchUserData = async (token) => {
    try {
      const userData = await authenticateUser(token);
      Cookies.set("first_name", userData.first_name, { expires: 1 });
      Cookies.set("last_name", userData.last_name, { expires: 1 });
    } catch (error) {
      console.error("Failed to fetch user data", error);
    }
  };

  const handleSendEmail = async () => {
    if (!userData.email) {
      AlertError("Error", "Please enter your email");
      return;
    }

    try {
      await SendMailForgotPassword(userData.email);
      AlertSuccess("Success", "Password reset email sent");
      setMode("verify-otp");
    } catch (error) {
      AlertError("Error", "Failed to send email. Please try again.");
    }
  };

  const handleVerifyOTP = async () => {
    // Implement OTP verification logic
    if (!userData.otp) {
      AlertError("Error", "Please enter OTP");
      return;
    }

    try {
      // Add OTP verification service call
      const res = await verifyOTP(userData.email,userData.otp)
      setMode("reset-password");
    } catch (error) {
      AlertError("Error", "Invalid OTP");
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword.password || !newPassword.confirmPassword) {
      AlertError("Error", "Please enter both password fields");
      return;
    }

    if (newPassword.password !== newPassword.confirmPassword) {
      AlertError("Error", "Passwords do not match");
      return;
    }

    try {
      const res = await ResetPassword({
        email: userData.email,
        password: newPassword.password,
        otp:userData.otp
      })
      setUserData({
        username: "",
        password: "",
        email: "",
        otp: ""
      })
      AlertSuccess("Success", "Password reset successfully");
      setMode("login");
    } catch (error) {
      AlertError("Error", "Failed to reset password");
    }
  };

  const renderContent = () => {
    switch(mode) {
      case "login":
        return renderLoginForm();
      case "forgotpassword":
        return renderForgotPasswordForm();
      case "verify-otp":
        return renderOTPForm();
      case "reset-password":
        return renderResetPasswordForm();
      default:
        return renderLoginForm();
    }
  };

  const renderLoginForm = () => (
    <Box>
      <Typography
        variant="h5"
        component="h2"
        gutterBottom
        color="#1976d2"
        fontWeight="bold"
        fontSize="32px"
      >
        Sign in
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        สำหรับผู้ดูแลระบบ หรือ เจ้าหน้าที่ที่เกี่ยวข้องเท่านั้น!
      </Typography>
      <TextField
        label="User Name"
        variant="outlined"
        margin="normal"
        fullWidth
        name="username"
        value={userData.username}
        onChange={handleInputChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <FaRegUser fontSize="24px" />
            </InputAdornment>
          ),
        }}
      />
      <TextField
        label="Password"
        type="password"
        variant="outlined"
        margin="normal"
        fullWidth
        name="password"
        value={userData.password}
        onChange={handleInputChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <RiLockPasswordLine fontSize="24px" />
            </InputAdornment>
          ),
        }}
      />
      <Button
        variant="contained"
        color="primary"
        size="large"
        fullWidth
        onClick={handleLogin}
        sx={{ mt: 2 }}
      >
        Sign in
      </Button>
      <Button
        variant="text"
        fullWidth
        onClick={() => setMode("forgotpassword")}
        sx={{ mt: 1 }}
      >
        Forgot Password?
      </Button>
    </Box>
  );

  const renderForgotPasswordForm = () => (
    <Box>
      <Typography
        variant="h5"
        component="h2"
        gutterBottom
        color="#1976d2"
        fontWeight="bold"
        fontSize="32px"
      >
        Forgot Password
      </Typography>
      <TextField
        label="Email"
        variant="outlined"
        margin="normal"
        fullWidth
        name="email"
        value={userData.email}
        onChange={handleInputChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <MdOutlineEmail fontSize="24px" />
            </InputAdornment>
          ),
        }}
      />
      <Button
        variant="contained"
        color="primary"
        size="large"
        fullWidth
        onClick={handleSendEmail}
        sx={{ mt: 2 }}
      >
        Send Reset Link
      </Button>
      <Button
        variant="text"
        fullWidth
        onClick={() => setMode("login")}
        sx={{ mt: 1 }}
      >
        Back to Login
      </Button>
    </Box>
  );

  const renderOTPForm = () => (
    <Box>
      <Typography
        variant="h5"
        component="h2"
        gutterBottom
        color="#1976d2"
        fontWeight="bold"
        fontSize="32px"
      >
        Verify OTP
      </Typography>
      <TextField
        label="OTP"
        variant="outlined"
        margin="normal"
        fullWidth
        name="otp"
        value={userData.otp}
        onChange={handleInputChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <MdOutlineEmail fontSize="24px" />
            </InputAdornment>
          ),
        }}
      />
      <Button
        variant="contained"
        color="primary"
        size="large"
        fullWidth
        onClick={handleVerifyOTP}
        sx={{ mt: 2 }}
      >
        Verify
      </Button>
      <Button
        variant="text"
        fullWidth
        onClick={() => setMode("login")}
        sx={{ mt: 1 }}
      >
        Back to Login
      </Button>
    </Box>
  );

  const renderResetPasswordForm = () => (
    <Box>
      <Typography
        variant="h5"
        component="h2"
        gutterBottom
        color="#1976d2"
        fontWeight="bold"
        fontSize="32px"
      >
        Reset Password
      </Typography>
      <TextField
        label="New Password"
        type={passwordVisibility.password ? "text" : "password"}
        variant="outlined"
        margin="normal"
        fullWidth
        name="password"
        value={newPassword.password}
        onChange={handlePasswordChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <RiLockPasswordLine fontSize="24px" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => togglePasswordVisibility('password')}
                edge="end"
              >
                {passwordVisibility.password ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <TextField
        label="Confirm Password"
        type={passwordVisibility.confirmPassword ? "text" : "password"}
        variant="outlined"
        margin="normal"
        fullWidth
        name="confirmPassword"
        value={newPassword.confirmPassword}
        onChange={handlePasswordChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <RiLockPasswordLine fontSize="24px" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => togglePasswordVisibility('confirmPassword')}
                edge="end"
              >
                {passwordVisibility.confirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Button
        variant="contained"
        color="primary"
        size="large"
        fullWidth
        onClick={handleResetPassword}
        sx={{ mt: 2 }}
      >
        Reset Password
      </Button>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        backgroundImage: `url(${bg_login})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        height: "100vh",
        color: "white",
      }}
    >
      <Box
        sx={{
          flex: isMobile ? "none" : 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          p: isMobile ? 2 : 4,
          textAlign: isMobile ? "center" : "left",
          margin: "80px",
        }}
      >
        <Typography
          variant={isMobile ? "h4" : "h3"}
          component="h1"
          gutterBottom
        >
          WELCOME
        </Typography>
        <Typography
          variant={isMobile ? "h5" : "h4"}
          component="h2"
          gutterBottom
        >
          TicketShare Boat Rental Admin
        </Typography>
      </Box>
      <Box
        sx={{
          width: isMobile ? "100%" : 500,
          paddingLeft: "50px !important",
          paddingRight: "50px !important",
          bgcolor: "white",
          p: isMobile ? 2 : 4,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        {renderContent()}
      </Box>
    </Box>
  );
};

export default LoginPage;